import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http'
import { inject } from '@angular/core'
import { Router } from '@angular/router'
import { ModalController } from '@ionic/angular/standalone'
import { catchError, Observable, switchMap, throwError } from 'rxjs'

import { VpnBlockedService } from '../../pages/vpn-blocked/vpn-blocked.service'
import { ToastService } from '../components/toast/toast.service'
import { TOAST_STATE } from '../components/toast/types/toast.type'
import { AuthService } from '../services/auth.service'
import { TokenRenewalService } from '../services/token-renewal.service'

const isAuthBypassUrl = (url: string): boolean => {
	return (
		url.includes('/auth/user_login') ||
		url.includes('/auth/demo_login') ||
		url.includes('/auth/renew') ||
		url.includes('renew') ||
		url.includes('/auth/logout') ||
		url.includes('logout')
	)
}

const isMaintenanceError = (err: HttpErrorResponse): boolean => {
	return err.status === 401 && typeof err.error?.error === 'string' && err.error.error.includes('DETAIL: E0001')
}

const isLoginServerError = (err: HttpErrorResponse, isLogin: boolean): boolean => {
	return isLogin && err.status >= 500 && err.status < 600
}

const isVpnBlockedError = (err: HttpErrorResponse): boolean => {
	if (err.status !== 403) {
		return false
	}
	const code = err.error?.code
	return code === 'VPN_DETECTED' || code === 'VPN_BLOCKED'
}

const handleVpnBlocked = (
	err: HttpErrorResponse,
	authService: AuthService,
	vpnBlockedService: VpnBlockedService,
	modalController: ModalController,
	router: Router,
): void => {
	authService.clearUserData()
	if (err.error) {
		vpnBlockedService.setVpnInfo(err.error)
	}
	void modalController?.getTop?.()?.then((topModal) => topModal?.dismiss?.())
	router.navigateByUrl('/vpn-blocked')
}

const isConnectionError = (err: HttpErrorResponse): boolean => {
	return err.error instanceof ProgressEvent && err.error.type === 'error'
}

const shouldRedirectHomeOn401 = (err: HttpErrorResponse, isLogin: boolean, isDemoLogin: boolean, isRenew: boolean): boolean => {
	return err.status === 401 && !isLogin && !isDemoLogin && !isRenew
}

const canRenewToken = (err: HttpErrorResponse, reqUrl: string, authService: AuthService): boolean => {
	if (err.status !== 401 || isAuthBypassUrl(reqUrl) || authService.isDemoUser()) {
		return false
	}
	return Boolean(authService.getRefreshToken())
}

const clearUserAndRedirect = (authService: AuthService, router: Router, targetUrl: string): void => {
	authService.clearUserData()
	if (router.url !== targetUrl) {
		router.navigateByUrl(targetUrl)
	}
}

const handleTokenRenewal = (
	req: HttpRequest<unknown>,
	next: HttpHandlerFn,
	err: HttpErrorResponse,
	authService: AuthService,
	tokenRenewalService: TokenRenewalService,
	router: Router,
): Observable<HttpEvent<unknown>> => {
	return tokenRenewalService.renewToken().pipe(
		switchMap((renewed: boolean) => {
			if (renewed) {
				const accessToken = authService.getAccessToken()
				const retriedReq = accessToken ? req.clone({ headers: req.headers.set('Authorization', `Bearer ${accessToken}`) }) : req
				return next(retriedReq)
			}
			clearUserAndRedirect(authService, router, '/home')
			return throwError(() => err)
		}),
	)
}

const handleGeneralErrorToast = (err: HttpErrorResponse, toastService: ToastService, isLogin: boolean): void => {
	if (isLogin || typeof err.error?.error !== 'string') {
		return
	}
	const formattedError = extractErrorMessage(err.error.error)
	toastService.showToast(TOAST_STATE.DANGER, formattedError ?? 'Contact administrator for further support.')
}

const extractErrorMessage = (text: string): string | null => {
	const regex = /ERROR: [^\n]+/
	const match = regex.exec(text)
	return match ? match[0] : null
}

export const httpErrorsInterceptor: HttpInterceptorFn = (req, next) => {
	const toastService = inject(ToastService)
	const authService = inject(AuthService)
	const router = inject(Router)
	const tokenRenewalService = inject(TokenRenewalService)
	const vpnBlockedService = inject(VpnBlockedService)
	const modalController = inject(ModalController)
	const isLogin = req.url.includes('/auth/user_login')
	const isDemoLogin = req.url.includes('/auth/demo_login')
	const isRenew = req.url.includes('/auth/renew') || req.url.includes('renew')

	return next(req).pipe(
		catchError((err) => {
			if (!(err instanceof HttpErrorResponse)) {
				return throwError(() => err)
			}

			if (isLoginServerError(err, isLogin)) {
				router.navigateByUrl('/maintenance')
			} else if (isVpnBlockedError(err)) {
				handleVpnBlocked(err, authService, vpnBlockedService, modalController, router)
			} else if (isConnectionError(err)) {
				toastService.showToast(TOAST_STATE.DANGER, 'Disconnected: Please refresh the page')
			} else if (isMaintenanceError(err)) {
				clearUserAndRedirect(authService, router, '/maintenance')
			} else if (canRenewToken(err, req.url, authService)) {
				return handleTokenRenewal(req, next, err, authService, tokenRenewalService, router)
			} else if (shouldRedirectHomeOn401(err, isLogin, isDemoLogin, isRenew)) {
				clearUserAndRedirect(authService, router, '/home')
			} else {
				handleGeneralErrorToast(err, toastService, isLogin)
			}

			return throwError(() => err)
		}),
	)
}
